import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n";
import { api, ApiFailure } from "../lib/api";
import { Icon } from "../components/Icon";
import { PublicFrame } from "./Signup";

// The owner of a business a Super Admin added (migration 053) chooses their own password here, with the setup code
// they were given or sent. The code may come in the link's fragment (#code=…), which the browser never sends to the
// server; it is single use and expires. Nobody else, the Super Admin included, ever sees or chooses the password.

const fromHash = () => {
  const m = /(?:^#|&)code=([A-Za-z0-9-]{12,16})/.exec(window.location.hash);
  return m ? m[1]!.toUpperCase() : "";
};

export function OwnerSetup() {
  const { t } = useLang();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState(fromHash);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!/^05\d{8}$/.test(phone.trim())) { setErr(t("fieldMobileFormat")); return; }
    if (!/^[A-Z0-9]{12}$/.test(code.toUpperCase().replace(/[\s-]/g, ""))) { setErr(t("osCodeFormat")); return; }
    if (pw.length < 10 || !/\d/.test(pw) || !/[A-Za-z؀-ۿ]/.test(pw)) { setErr(t("fieldPasswordRule")); return; }
    if (pw !== pw2) { setErr(t("osMismatch")); return; }
    setBusy(true);
    try {
      await api("POST", "/owner-setup/complete", { phone: phone.trim(), code: code.trim(), password: pw });
      // The code leaves the address bar once used.
      if (window.location.hash) history.replaceState(null, "", window.location.pathname + window.location.search);
      setDone(true);
    } catch (x) {
      setErr(x instanceof ApiFailure
        ? x.code === "too_many_attempts" ? t("signupTooMany") : x.code === "validation" ? t(x.fields.password === "password_is_mobile" ? "fieldPasswordIsMobile" : "fieldPasswordRule") : t("osInvalid")
        : t("serverError"));
      setPw(""); setPw2("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PublicFrame title={t("osTitle")} lede={t("osLede")}>
      {done ? (
        <div className="su-status"><div className="dlg-alert show" role="status" data-testid="setup-done"><Icon name="check" /><span>{t("osDone")}</span></div>
          <Link className="btn btn-primary" to="/login">{t("sstGoSignIn")}</Link></div>
      ) : (
        <form noValidate onSubmit={submit} data-testid="owner-setup">
          <div className="field"><label htmlFor="os-m">{t("osMobile")}</label>
            <input id="os-m" className="control num" dir="ltr" style={{ textAlign: "start" }} type="tel" autoComplete="username" placeholder={t("mobilePh")} value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
          <div className="field"><label htmlFor="os-c">{t("osCode")}</label>
            <input id="os-c" className="control num" dir="ltr" style={{ textAlign: "start", letterSpacing: "1px" }} autoComplete="one-time-code" autoCapitalize="characters" maxLength={16} placeholder="ABCD-EFGH-JKLM"
              value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} /></div>
          <div className="field"><label htmlFor="os-p">{t("osPassword")}</label>
            <input id="os-p" className="control" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
            <span className="hint">{t("signupPasswordHint")}</span></div>
          <div className="field"><label htmlFor="os-p2">{t("osPassword2")}</label>
            <input id="os-p2" className="control" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
          {err && <div className="dlg-alert show" role="alert" data-testid="setup-error"><Icon name="alert" /><span>{err}</span></div>}
          <button className="btn btn-primary" type="submit" disabled={busy}>{t("osSubmit")}</button>
        </form>
      )}
      <p className="login-alt"><Link to="/login">{t("signupSignIn")}</Link></p>
    </PublicFrame>
  );
}
